/**
 * ตัวช่วยตั้งค่าระบบ (Setup Wizard) — ร้อย 5 ขั้นตอนตั้งค่าหลักให้เป็น "เส้นเดียว"
 * สำหรับ owner ที่ไม่มี HR: เห็นทั้งเส้นทาง + ขั้นที่ต้องทำต่อ + ทำไมขั้นนั้นสำคัญ
 * (กันพลาด/ผิดกฎหมาย) แล้วจบที่ "ตรวจทาน" = พิมพ์เขียวการทำเงินเดือน.
 *
 * ไม่ rebuild ฟอร์ม — แต่ละขั้นลิงก์ไปหน้าตั้งค่าจริงที่มีอยู่แล้ว แล้วกลับมาเห็น
 * สถานะอัปเดต (จาก setup checklist จริง). พอครบ → ปุ่มไปดูพิมพ์เขียว.
 */
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Pencil, Sparkles, Check } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Stepper, type FlowStep } from '../../components/ui/GuidedFlow';
import { ComplianceAlert } from '../../components/ui/ComplianceAlert';
import { useSetupChecklist, useAckSetupStep } from '../../hooks';

/** ทำไมแต่ละขั้นสำคัญ — ภาษาคนไม่ใช่ HR เข้าใจ (ระบบรู้กฎหมายแทน) */
const WHY: Record<string, string> = {
  employees: 'ต้องมีพนักงานในระบบก่อน ถึงจะคำนวณเวลา/เงินเดือนให้ได้',
  salary: 'ทุกคนต้องมีฐานเงินเดือน — ถ้าไม่ครบ ระบบจะปิดงวดไม่ได้ (กันจ่ายผิด)',
  quota: 'สร้าง “แพคเกจสิทธิ์การลา” (ป่วย/กิจ/พักร้อน) ครั้งเดียว แล้วใส่ให้พนักงานเป็นกลุ่ม — ระบบ prorate คนเข้ากลางปีให้เอง และหักวันลา/คิดลาไม่รับเงินได้ถูกต้อง',
  approval: 'กำหนดว่าใครอนุมัติ/รับแจ้งใบลา–OT (ตั้งหัวหน้างานให้พนักงาน หรือสายอนุมัติ) เปลี่ยนภายหลังได้เสมอ — ถ้าไม่ตั้ง ระบบจะแจ้ง HR ก่อน ไม่มี HR ก็แจ้งเจ้าของ',
  holidays: 'ตั้งวันหยุดบริษัท ระบบจะคิดค่าทำงานวันหยุด/OT วันหยุดให้ถูก',
  time: 'ตั้งวันเวลาทำงาน (วันทำงาน/สัปดาห์ ชั่วโมง/วัน อนุโลมมาสาย เพดาน OT) — เป็นฐานให้ระบบคำนวณเวลาเข้างาน สาย และ OT',
  shifts: 'ถ้าบริษัทเข้างานเป็นกะ/หมุนเวร ให้สร้างกะไว้ (ไม่บังคับ — ถ้าทุกคนเข้างานเวลาเดียวกันข้ามได้)',
  notifications: 'เลือกว่าจะให้ระบบแจ้งเตือนอะไรเข้า LINE บ้าง (ไม่บังคับ — มีค่ามาตรฐานให้อยู่แล้ว ปรับทีหลังได้)',
};

export function OwnerSetup() {
  const { data, isLoading } = useSetupChecklist();
  const ack = useAckSetupStep();
  const items = data?.items ?? [];
  const steps: FlowStep[] = items.map((i) => ({ id: i.key, label: i.label }));
  const current = data ? (data.complete ? items.length : items.findIndex((i) => !i.done)) : 0;

  return (
    <AdminLayout title="ตัวช่วยตั้งค่าระบบ">
      <div className="max-w-2xl mx-auto space-y-4 pb-10">
        <p className="text-sm text-gray-500 px-1">
          ตั้งค่าให้ครบทีละขั้น — ระบบจะบอกว่าต้องทำอะไรต่อ และทำไมขั้นนั้นถึงสำคัญ
          เสร็จแล้วดู “พิมพ์เขียว” เพื่อตรวจทานทั้งระบบก่อนเริ่มใช้งานจริง
        </p>

        {isLoading && <div className="card p-6 text-center text-gray-400">กำลังโหลด…</div>}

        {data && (
          <>
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-900">ความคืบหน้า</span>
                <span className="text-sm font-medium text-brand-700">{data.pct}%</span>
              </div>
              <Stepper steps={steps} current={current} />
            </div>

            {/* รายการขั้น — ขั้นที่ต้องทำต่อจะถูกเน้น */}
            <div className="space-y-2">
              {items.map((it) => {
                const isNext = !it.done && it.key === data.nextStep?.key;
                return (
                  <div key={it.key} className={`card p-4 ${isNext ? 'ring-2 ring-teal' : ''}`}>
                    <div className="flex items-start gap-3">
                      {it.done
                        ? <CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" />
                        : <span className={`w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 ${isNext ? 'border-teal' : 'border-gray-300'}`} />}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {it.label}
                          {it.optional && <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">ไม่บังคับ</span>}
                        </p>
                        {!it.done && <p className="text-sm text-gray-500 mt-0.5">{WHY[it.key] ?? ''}</p>}
                        {it.done && it.manual && <p className="text-xs text-gray-400 mt-0.5">ทำเครื่องหมายเองว่าเสร็จ</p>}
                      </div>
                      {it.done ? (
                        <div className="flex items-center gap-3 shrink-0">
                          {it.manual && (
                            <button
                              onClick={() => ack.mutate({ key: it.key, done: false })}
                              disabled={ack.isPending}
                              className="text-xs text-gray-400 hover:text-gray-600"
                            >
                              ยกเลิก
                            </button>
                          )}
                          <Link to={it.route} className="text-sm text-gray-400 hover:text-teal inline-flex items-center gap-1">
                            <Pencil className="w-3.5 h-3.5" /> แก้ไข
                          </Link>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => ack.mutate({ key: it.key, done: true })}
                            disabled={ack.isPending}
                            title="ทำเครื่องหมายว่าทำเสร็จแล้ว (เผื่อทำนอกระบบ/ไม่ต้องใช้ขั้นนี้)"
                            className="btn-secondary inline-flex items-center gap-1 text-sm"
                          >
                            <Check className="w-4 h-4" /> ทำแล้ว
                          </button>
                          <Link to={it.route} className={`${isNext ? 'btn-cta' : 'btn-secondary'} inline-flex items-center gap-1`}>
                            ไปตั้งค่า <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* จบเส้นทาง → ตรวจทานพิมพ์เขียว */}
            {data.complete ? (
              <div className="card p-5 bg-teal-50/40 text-center space-y-3">
                <CheckCircle2 className="w-8 h-8 text-teal mx-auto" />
                <p className="font-semibold text-gray-900">ตั้งค่าหลักครบแล้ว 🎉</p>
                <p className="text-sm text-gray-600">ตรวจทานพิมพ์เขียวการทำเงินเดือนทั้งระบบก่อนเริ่มใช้งานจริง</p>
                <Link to="/owner/blueprint" className="btn-cta w-full justify-center flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> ดูพิมพ์เขียว (ตรวจทาน)
                </Link>
              </div>
            ) : (
              <ComplianceAlert
                level="info"
                title="ดูภาพรวมได้ตลอด"
                detail="กดดู “พิมพ์เขียวการทำเงินเดือน” เพื่อเห็นว่าตอนนี้ตั้งค่าอะไรไปแล้วบ้าง + ยังขาดอะไร"
                action={{ label: 'เปิดพิมพ์เขียว', to: '/owner/blueprint' }}
              />
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
